import { Badge, Button, Chip, IconButton, Tooltip } from "@mui/material";
import {
  CheckCircleOutline,
  EditOutlined,
  NoteOutlined,
  StickyNote2Outlined,
} from "@mui/icons-material";
import { formatMoney } from "utils/utils";
import { formatDisplayDate } from "utils/dateFormat";
import {
  admissionTermRowFigures,
  countedBonus,
  countedCommission,
  isInstallmentBonusClaimed,
  isInstallmentCommissionClaimed,
  toNumber,
} from "./accountUtils";

const parseTermNumber = (value, fallback) => {
  const parsed = Number(
    String(value ?? fallback)
      .trim()
      .replace(/^T/i, "")
  );
  return Number.isFinite(parsed) ? parsed : fallback;
};

const MoneyCell = ({ value, colorize = false }) => {
  const number = toNumber(value);
  const color = !colorize ? "" : number >= 0 ? "text-green-600" : "text-red-600";
  return <span className={`tabular-nums font-medium ${color}`}>{formatMoney(number)}</span>;
};

const ClaimedAmountCell = ({ value, claimed, notApplicable, label }) => (
  <div className="flex items-center justify-end gap-1">
    <MoneyCell value={value} />
    {notApplicable ? (
      <Tooltip title={`${label} marked not applicable`} arrow>
        <span className="rounded bg-app-surface-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase text-app-text-secondary">
          N/A
        </span>
      </Tooltip>
    ) : (
      claimed && (
        <Tooltip title={`${label} claimed`} arrow>
          <CheckCircleOutline sx={{ fontSize: 15 }} className="text-green-600" />
        </Tooltip>
      )
    )}
  </div>
);

const CompletedInstallmentsSummary = ({
  rows = [],
  termNotesCounts = {},
  canViewAccountNotes = true,
  onOpenNotes,
  onOpenTermNotes,
  onEdit,
}) => {
  const sortedRows = [...rows].sort(
    (a, b) => parseTermNumber(a?.terms, 0) - parseTermNumber(b?.terms, 0)
  );

  const totals = sortedRows.reduce(
    (acc, row) => ({
      fee: acc.fee + toNumber(row.perSemFee),
      discount: acc.discount + toNumber(row.discount),
      commission: acc.commission + countedCommission(row),
      bonus: acc.bonus + countedBonus(row),
      paid: acc.paid + toNumber(row.paidAmount),
      profitLoss: acc.profitLoss + admissionTermRowFigures(row).profitLoss,
    }),
    { fee: 0, discount: 0, commission: 0, bonus: 0, paid: 0, profitLoss: 0 }
  );

  const headerCell =
    "px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-app-text-secondary";
  const bodyCell = "px-3 py-2 text-sm text-app-text";

  return (
    <div className="rounded-lg border border-app-border bg-app-surface overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-app-border bg-app-surface-muted/40">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-medium text-app-text">Installments Summary</h2>
          <Tooltip
            title="Every term imported and paid, with commission & bonus claimed or marked N/A — shown as a read-only summary"
            arrow
          >
            <Chip
              label="Completed"
              size="small"
              color="success"
              sx={{ fontSize: 10, height: 20, fontWeight: 600 }}
            />
          </Tooltip>
        </div>
        {onEdit && (
          <Button size="small" variant="outlined" startIcon={<EditOutlined />} onClick={onEdit}>
            Open full editor
          </Button>
        )}
      </div>

      {sortedRows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-app-text-secondary">No installments found.</p>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-app-surface-muted/60 border-b border-app-border">
              <th className={`${headerCell} text-left`}>Date</th>
              <th className={`${headerCell} text-left`}>Term</th>
              <th className={`${headerCell} text-right`}>Fee</th>
              <th className={`${headerCell} text-right`}>Discount</th>
              <th className={`${headerCell} text-right`}>Commission</th>
              <th className={`${headerCell} text-right`}>Bonus</th>
              <th className={`${headerCell} text-right`}>Paid</th>
              <th className={`${headerCell} text-right`}>Profit/Loss</th>
              <th className={`${headerCell} text-center`}>Notes</th>
              <th className={`${headerCell} text-center`}>Term Notes</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, index) => {
              const termNumber = parseTermNumber(row?.terms, index + 1);
              const termNotesCount = termNotesCounts[termNumber - 1] || 0;
              const notesCount = row?.notes?.length || 0;
              const notesAvailable = Boolean(row?.id) && !row?._unimported;
              return (
                <tr
                  key={row?.uuid || row?.id || termNumber}
                  className="border-b border-app-border last:border-b-0 hover:bg-app-surface-muted/40"
                >
                  <td className={bodyCell}>
                    <Tooltip title={`Census date: ${formatDisplayDate(row?.censusDate)}`} arrow>
                      <span className="tabular-nums">{formatDisplayDate(row?.date)}</span>
                    </Tooltip>
                  </td>
                  <td className={bodyCell}>
                    <span className="inline-flex items-center rounded bg-app-surface-muted px-2 py-0.5 text-xs font-semibold">
                      T{termNumber}
                    </span>
                  </td>
                  <td className={`${bodyCell} text-right`}>
                    <MoneyCell value={row?.perSemFee} />
                  </td>
                  <td className={`${bodyCell} text-right`}>
                    <MoneyCell value={row?.discount} />
                  </td>
                  <td className={`${bodyCell} text-right`}>
                    <ClaimedAmountCell
                      value={row?.commission}
                      claimed={isInstallmentCommissionClaimed(row)}
                      notApplicable={Boolean(row?.commissionNotApplicable)}
                      label="Commission"
                    />
                  </td>
                  <td className={`${bodyCell} text-right`}>
                    <ClaimedAmountCell
                      value={row?.bonus}
                      claimed={isInstallmentBonusClaimed(row)}
                      notApplicable={Boolean(row?.bonusNotApplicable)}
                      label="Bonus"
                    />
                  </td>
                  <td className={`${bodyCell} text-right`}>
                    <MoneyCell value={row?.paidAmount} />
                  </td>
                  <td className={`${bodyCell} text-right`}>
                    <MoneyCell value={row?.profitLoss} colorize />
                  </td>
                  <td className={`${bodyCell} text-center`}>
                    <Tooltip
                      title={
                        !canViewAccountNotes
                          ? "You need the Accounts permission to view account notes"
                          : notesAvailable
                          ? "View installment notes"
                          : "No saved installment yet"
                      }
                      arrow
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={!canViewAccountNotes || !notesAvailable}
                          onClick={() => onOpenNotes?.(row)}
                        >
                          <Badge
                            badgeContent={canViewAccountNotes ? notesCount : 0}
                            color="error"
                            max={99}
                            invisible={!canViewAccountNotes}
                          >
                            <NoteOutlined
                              fontSize="small"
                              className={canViewAccountNotes ? "text-orange-400" : "text-gray-300"}
                            />
                          </Badge>
                        </IconButton>
                      </span>
                    </Tooltip>
                  </td>
                  <td className={`${bodyCell} text-center`}>
                    <Tooltip title="View term notes" arrow>
                      <IconButton size="small" onClick={() => onOpenTermNotes?.(row, index)}>
                        <Badge badgeContent={termNotesCount} color="error" max={99}>
                          <StickyNote2Outlined fontSize="small" className="text-orange-400" />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-app-border bg-app-surface-muted/60">
              <td className={`${bodyCell} font-semibold`} colSpan={2}>
                Total
              </td>
              <td className={`${bodyCell} text-right`}>
                <MoneyCell value={totals.fee} />
              </td>
              <td className={`${bodyCell} text-right`}>
                <MoneyCell value={totals.discount} />
              </td>
              <td className={`${bodyCell} text-right`}>
                <MoneyCell value={totals.commission} />
              </td>
              <td className={`${bodyCell} text-right`}>
                <MoneyCell value={totals.bonus} />
              </td>
              <td className={`${bodyCell} text-right`}>
                <MoneyCell value={totals.paid} />
              </td>
              <td className={`${bodyCell} text-right`}>
                <MoneyCell value={totals.profitLoss} colorize />
              </td>
              <td className={bodyCell} colSpan={2} />
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
};

export default CompletedInstallmentsSummary;
